/* eslint-disable @typescript-eslint/no-explicit-any */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DcrType, EntityType } from '@/core/graphql/generated/types';

import { HcoDcrSectionFields } from '../_constants/HcoDcrSectionsFields';
import { HcpDcrSectionFields } from '../_constants/HcpDcrSectionsFields';
import { useDcrFields } from '../_constants/useDcrFields';
import type { HcoDcrValuesIn } from '../_schemas/hcoSchema';
import type { HcpDcrValuesIn } from '../_schemas/hcpSchema';
import { getDirtyValues } from '../_utils/getFormDirtyValues';

import { reviewModalContentStyles as sx } from './ReviewModalContent.styles';
import { ReviewSection } from './ReviewSection/ReviewSection';

type Props = {
	entityType: EntityType;
	dcrType: DcrType;
};

export const ReviewModalContent = ({ entityType, dcrType }: Props) => {
	const { t } = useTranslation();
	const { allFields } = useDcrFields();

	const {
		control,
		formState: { dirtyFields, defaultValues }
	} = useFormContext<HcpDcrValuesIn | HcoDcrValuesIn>();

	const sectionFields =
		entityType === EntityType.Hcp ? HcpDcrSectionFields : HcoDcrSectionFields;

	const currentValues = useWatch({ control });

	const changedValues = useMemo(
		() => getDirtyValues(dirtyFields, currentValues, defaultValues),
		[dirtyFields, currentValues, defaultValues]
	);

	if (Object.keys(changedValues as any).length === 0) {
		return (
			<Typography sx={sx.noChangesText}>{t('No changes detected.')}</Typography>
		);
	}

	return (
		<Box sx={sx.root}>
			{Object.entries(sectionFields).map(([sectionName, fields]) => (
				<ReviewSection
					key={sectionName}
					sectionName={sectionName}
					fields={fields}
					changedValues={changedValues}
					defaultValues={defaultValues}
					currentValues={currentValues}
					fieldTranslations={allFields}
					dcrType={dcrType}
				/>
			))}
		</Box>
	);
};
